import { unpackToDateTime } from '@campfire/hot-date';
import { ContentState, convertFromRaw } from 'draft-js';
import { DateTime } from 'luxon';
import { BulletinManagement } from './__generated__/BulletinManagement';

export const bulletinContentState = (content: string): ContentState | null => {
  try {
    return convertFromRaw(JSON.parse(content));
  } catch (error) {
    return null;
  }
};

// Parsing draft-js raw json is the expensive part of both the row snippet and the
// search filter, so the result is kept per content string rather than redone per keystroke.
const plainTextCache = new Map<string, string>();

export const bulletinPlainText = (content: string): string => {
  const cached = plainTextCache.get(content);
  if (cached !== undefined) return cached;

  const contentState = bulletinContentState(content);
  const plainText = contentState
    ? contentState
        .getPlainText(' ')
        .replace(/\s+/g, ' ')
        .trim()
    : '';

  plainTextCache.set(content, plainText);
  return plainText;
};

export const audienceNames = (bulletin: BulletinManagement): string[] => [
  ...bulletin.programs.map((program) => program.name),
  ...bulletin.activities.map((activity) => activity.name),
  ...bulletin.activityRoles.map((role) => role.name),
];

export const bulletinAuthor = (bulletin: BulletinManagement): string => {
  const author = bulletin.lastUpdatedBy ?? bulletin.createdBy;
  return author ? `${author.profile.preferredName} ${author.profile.lastName}` : 'Unknown author';
};

export const bulletinDateLabel = (dateCreated: string): string => {
  const posted = unpackToDateTime(dateCreated);
  return posted.year === DateTime.local().year ? posted.toFormat('d LLL') : posted.toFormat('d LLL yy');
};

export const bulletinMatchesSearch = (bulletin: BulletinManagement, search: string): boolean => {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return [bulletin.title, bulletinPlainText(bulletin.content), bulletinAuthor(bulletin), ...audienceNames(bulletin)]
    .join(' ')
    .toLowerCase()
    .includes(term);
};
